import baseService from '@/services/baseService'
import LocalStorage from 'quasar/src/plugins/storage/LocalStorage.js';

const DEFAULT_CACHE_DURATION = 60 * 60 * 1000 // 1 hour in ms
const CACHE_PREFIX = 'app_cache:' // namespace to avoid collisions

/**
 * Factory to create a cache service with a configurable default duration.
 *
 * @param {Object} options
 * @param {number} [options.cacheDuration] - Default cache duration in ms
 */
export default function createCacheService(options = {}) {
  const defaultCacheDuration = options.cacheDuration ?? DEFAULT_CACHE_DURATION

  function getStorageKey(key) {
    return `${CACHE_PREFIX}${key}`
  }

  /**
   * Remove all cache entries that match the given prefix.
   *
   * @param {string} prefix
   */
  function flushLocalCacheByPrefix(prefix) {
    const keys = LocalStorage.getAllKeys()

    keys.forEach((key) => {
      if (key.startsWith(prefix)) {
        LocalStorage.remove(key)
      }
    })
  }

  /**
   * Asynchronous garbage collector.
   * Runs in a separate tick and does not block get/set responses.
   */
  function runGarbageCollectorAsync() {
    setTimeout(() => {
      try {
        const now = Date.now()
        const keys = LocalStorage.getKeys()

        keys.forEach((key) => {
          if (!key.startsWith(CACHE_PREFIX)) {
            return
          }

          const entry = LocalStorage.getItem(key)

          if (!entry || typeof entry !== 'object') {
            return
          }

          const { timestamp, duration, expiresAt } = entry

          // Fallbacks if some field is missing
          const effectiveDuration =
            typeof duration === 'number' ? duration : defaultCacheDuration

          const effectiveExpiresAt =
            typeof expiresAt === 'number'
              ? expiresAt
              : (typeof timestamp === 'number'
                  ? timestamp + effectiveDuration
                  : now - 1) // force removal if corrupted

          if (now >= effectiveExpiresAt) {
            LocalStorage.remove(key)
          }
        })
      } catch (error) {
        // console.error('Cache garbage collector failed', error)
      }
    }, 0)
  }

  return {
    ...baseService,

    defaultCacheDuration,

    flushLocalCacheByPrefix,

    getBaseUrl() {
      return process.env.VUE_APP_CACHE_DEFAULT_API_URL
    },

    flushCacheAdsTxt() {
        // Flush local asynchronously (non-blocking)
        flushLocalCacheByPrefix(`${CACHE_PREFIX}`)
        return this.axiosPost('flush-cache-ads-txt', {})
    },

    /**
     * Get a cached value by key.
     * Returns null if not found or expired.
     *
     * @param {string} key
     * @returns {*|null}
     */
    getCache(key) {
      // Fire GC in the background
      runGarbageCollectorAsync()

      const storageKey = getStorageKey(key)
      const entry = LocalStorage.getItem(storageKey)

      if (!entry) {
        return null
      }

      const now = Date.now()
      const { data, timestamp, duration, expiresAt } = entry

      const effectiveDuration =
        typeof duration === 'number' ? duration : defaultCacheDuration

      const effectiveExpiresAt =
        typeof expiresAt === 'number'
          ? expiresAt
          : (typeof timestamp === 'number'
              ? timestamp + effectiveDuration
              : now - 1)

      if (now >= effectiveExpiresAt) {
        LocalStorage.remove(storageKey)
        return null
      }

      return data
    },

    /**
     * Set a cached value by key.
     * Optionally override duration for this specific entry.
     *
     * @param {string} key
     * @param {*} data
     * @param {number} [customDuration] - Duration in ms
     */
    setCache(key, data, customDuration) {
      const storageKey = getStorageKey(key)
      const now = Date.now()

      const duration =
        typeof customDuration === 'number' ? customDuration : defaultCacheDuration
      try {
        LocalStorage.set(storageKey, {
          data,
          timestamp: now,
          duration,
          expiresAt: now + duration
        })
      } catch (e) {
        // Quota exceeded or other storage error: best effort fallback.
        try {
          flushLocalCacheByPrefix(`${CACHE_PREFIX}`)
          LocalStorage.set(storageKey, {
            data,
            timestamp: now,
            duration,
            expiresAt: now + duration
          })
        } catch (e2) {
          // If it still fails, give up on caching silently.
        }
      }

      // Fire GC in the background
      runGarbageCollectorAsync()
    },

    /**
     * Expose a manual GC if you ever want to call it explicitly.
     */
    runGarbageCollector() {
      runGarbageCollectorAsync()
    }
  }
}
